import React, { useMemo, useState, useEffect, useCallback } from "react"

const QUESTIONS_PER_PAGE = 4

/**
 * NeedsObjectives.js — v7 (shared-nav cleanup)
 * ------------------------------------------------------
 * ✅ Objectives question = #1
 * ✅ 4 questions per page, per applicant
 * ✅ Currency field: type freely, formats on blur to $600,000.00
 * ✅ Autosaves to parent (updateCrmData/onUpdate) WITHOUT infinite loop
 * ✅ Internal page Next hides on last page
 * ✅ Local bottom Back/Next removed (shared nav handled in BrokerCRM)
 * ✅ Infinite-loop hydrate guard preserved
 */

const BRAND = {
  primary: "#4EDCF6",
  text: "#141414",
  light: "#EAFBFF",
  border: "#E6E6E6",
  grey: "#6B6B6B",
  danger: "#D11A2A",
  panelBlue: "#4EDCF6",
}

const OPTIONS = {
  loanPriority: [
    { value: "Cost", label: "Cost - I want the lowest interest rate options available" },
    {
      value: "Cashflow",
      label:
        "Cashflow - reducing my monthly outgoings after settlement is the most important thing to me",
    },
    { value: "Features", label: "Features - getting the loan features I want is the most important" },
    { value: "Lender", label: "Lender - selecting the right lender is the most important" },
    { value: "CustomerService", label: "Customer service - I want a lender who cares about me" },
    {
      value: "Environmental",
      label:
        "Environmental impact - choosing a lender who has a commitment to their environmental footprint is the most important thing",
    },
    { value: "Time", label: "Time - I need to obtain a loan by a specific date" },
    { value: "Other", label: "Other - something else and I will give more information" },
  ],

  mainReason: [
    { value: "PurchaseOwnerOcc", label: "Purchase a property to live in" },
    { value: "PurchaseInvestment", label: "Purchase a residential property for investment" },
    { value: "PurchaseLand", label: "Purchase of land" },
    { value: "HouseLandPackage", label: "Purchase a house and land package" },
    { value: "Refinance", label: "Refinance of existing home loan(s)" },
    { value: "DebtConsolidation", label: "Debt consolidation" },
    { value: "Construction", label: "Construction" },
    { value: "NonStructuralRenos", label: "Non-structural renovations to a property" },
    {
      value: "TopUp",
      label:
        "Top up on a current home loan (please advise purpose i.e. purchase of a car, holiday, wedding etc.)",
    },
    { value: "InvestOther", label: "Investment other than in property (such as shares etc)" },
    { value: "Bridging", label: "Bridging finance" },
    { value: "CommercialBusiness", label: "Commercial or business purposes" },
    { value: "Other", label: "Other (please specify in the box below)" },
  ],

  loanTerm: [
    { value: "MaxPossible", label: "Maximum loan term possible" },
    {
      value: "MatchExisting",
      label:
        "I am refinancing and/or increasing my existing loan and want to match my existing loan term",
    },
    { value: "SetTerm", label: "I have a set loan term I require" },
    { value: "Unsure", label: "Unsure, let's discuss options" },
  ],

  repaymentType: [
    { value: "PAndI", label: "Principal and Interest" },
    { value: "IO", label: "Interest Only" },
    { value: "Combo", label: "A combination of 'Principal and Interest' and 'Interest Only'" },
    { value: "IOInAdvance", label: "Interest Only in Advance" },
    { value: "Unsure", label: "Not sure, let's discuss what's best for me" },
  ],

  rateType: [
    { value: "Fixed", label: "Fixed" },
    { value: "Variable", label: "Variable" },
    { value: "Split", label: "Split loan (part fixed and part variable)" },
    { value: "Unsure", label: "Not sure, let's discuss what's best for me" },
  ],

  rateLockImportance: [
    { value: "MustHave", label: "Must Have" },
    { value: "Preferred", label: "Preferred but not a deal breaker" },
    { value: "NotImportant", label: "Not Important" },
    { value: "NotApplicable", label: "Not applicable - I want a variable rate loan" },
    { value: "Unsure", label: "Not sure, let's discuss what's best for me" },
  ],

  financeUnderstanding: [
    { value: "YesClear", label: "Yes - I have a clear picture of where my money goes each month" },
    {
      value: "Sometimes",
      label:
        "Sometimes - I have a good understanding, but it can change a lot from month to month",
    },
    { value: "NoTrack", label: "No - I don't keep track of where my money goes each month" },
  ],

  yesNoMaybe: [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
    { value: "Possibly", label: "Possibly, I would need to think about it" },
  ],

  yesNoMaybeNoDebts: [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
    { value: "Possibly", label: "Possibly, I would need to think about it" },
    { value: "NoDebts", label: "I don't have any existing debts" },
  ],

  yesNoDetails: [
    { value: "Yes", label: "Yes - please provide details below" },
    { value: "No", label: "No" },
  ],

  yesNo: [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ],

  yesNoUnsure: [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
    { value: "Unsure", label: "Unsure" },
  ],
}

const styles = {
  page: { maxWidth: 980, margin: "0 auto", padding: "24px 16px" },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
  },
  h2: { margin: 0, fontSize: 26, fontWeight: 800, color: BRAND.text },

  cardsRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 },
  card: {
    borderRadius: 18,
    border: `3px solid ${BRAND.primary}`,
    background: BRAND.light,
    padding: 18,
    minHeight: 120,
  },
  cardMuted: {
    borderRadius: 18,
    border: "3px dashed #D6D6D6",
    background: "#FAFAFA",
    padding: 18,
    minHeight: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#7A7A7A",
    fontWeight: 700,
  },
  cardTitle: { fontSize: 24, fontWeight: 900, margin: 0, color: BRAND.text },
  cardSub: { marginTop: 10, fontSize: 16, fontWeight: 800, color: BRAND.text },
  cardMeta: { marginTop: 6, fontSize: 14, color: BRAND.grey, fontWeight: 600 },

  section: {
    border: `1px solid ${BRAND.border}`,
    borderRadius: 14,
    padding: 18,
    marginBottom: 14,
    background: "#fff",
  },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 900, color: BRAND.text },

  field: { marginTop: 14 },
  label: { display: "block", fontSize: 18, fontWeight: 900, color: BRAND.text, marginBottom: 12 },
  requiredStar: { color: BRAND.danger, marginRight: 6, fontWeight: 900 },

  textarea: {
    width: "100%",
    minHeight: 140,
    borderRadius: 18,
    border: "2px solid #24303A",
    padding: 16,
    fontSize: 18,
    outline: "none",
    resize: "vertical",
  },
  input: {
    width: "100%",
    height: 64,
    borderRadius: 18,
    border: "2px solid #24303A",
    padding: "0 18px",
    fontSize: 22,
    outline: "none",
  },
  select: {
    width: "100%",
    height: 64,
    borderRadius: 18,
    border: "2px solid #24303A",
    padding: "0 18px",
    fontSize: 18,
    outline: "none",
    background: "#fff",
  },

  hint: { marginTop: 8, fontSize: 13, color: BRAND.grey, fontWeight: 600 },

  radioGroup: { display: "grid", gridTemplateColumns: "1fr", gap: 10 },
  radioOption: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 16,
    border: "1px solid #E3E3E3",
    borderRadius: 14,
    cursor: "pointer",
  },
  radioDot: { width: 18, height: 18 },

  pager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 16,
  },
  pagerText: { fontSize: 13, color: BRAND.grey, fontWeight: 800 },

  pagerBtn: {
    height: 56,
    minWidth: 220,
    padding: "0 26px",
    borderRadius: 999,
    border: "2px solid #DADADA",
    background: "#FFFFFF",
    color: "#141414",
    fontSize: 22,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  pagerBtnPrimary: {
    height: 56,
    minWidth: 260,
    padding: "0 30px",
    borderRadius: 999,
    border: "2px solid #141414",
    background: "#141414",
    color: "#FFFFFF",
    fontSize: 22,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  pagerBtnDisabled: {
    opacity: 0.35,
    cursor: "not-allowed",
  },

  errorBox: {
    marginTop: 14,
    border: "1px solid #FFD0D6",
    background: "#FFF3F5",
    color: "#8A0F1D",
    padding: 12,
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 800,
  },
}

const parseCurrencyToNumber = (raw) => {
  if (raw === null || raw === undefined) return null
  const s = String(raw).trim()
  if (!s) return null

  const cleaned = s.replace(/[^0-9.]/g, "")
  if (!cleaned) return null

  const parts = cleaned.split(".")
  const whole = parts[0] || "0"
  const dec = parts[1] || ""
  const normalized = dec.length ? `${whole}.${dec.slice(0, 2)}` : whole

  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}

const formatCurrencyAU = (n) => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return ""
  const fixed = Number(n).toFixed(2)
  const [whole, dec] = fixed.split(".")
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  return `$${withCommas}.${dec}`
}

const normaliseApplicants = (data) => {
  if (!data) return []
  if (Array.isArray(data.applicants) && data.applicants.length) return data.applicants
  const a1 = data.applicant1 ? { ...data.applicant1, applicantIndex: 1 } : null
  const a2 = data.applicant2 ? { ...data.applicant2, applicantIndex: 2 } : null
  const list = []
  if (a1) list.push(a1)
  if (a2) list.push(a2)
  return list
}

const hasApplicant2 = (apps) => {
  if (!apps || apps.length < 2) return false
  const a2 = apps[1] || {}
  return Boolean(
    a2.firstName ||
      a2.lastName ||
      a2.email ||
      a2.mobile ||
      a2.dateOfBirth ||
      a2.dob ||
      a2.isActiveApplicant
  )
}

const getApplicantLabel = (a, idx) => {
  const fn = (a?.firstName || "").trim()
  const ln = (a?.lastName || "").trim()
  const full = `${fn} ${ln}`.trim()
  return full || `Applicant ${idx}`
}

const getApplicantEmail = (a) => (a?.email || "").trim()

const buildQuestions = () => [
  {
    key: "objectives",
    type: "textarea",
    required: true,
    label: "What are your needs and objectives for this loan?",
    placeholder:
      "E.g. Buy my first home, refinance for a lower rate, renovate, consolidate debts, access equity…",
  },
  {
    key: "borrowAmount",
    type: "currency",
    required: true,
    label: "If known, what amount are you looking to borrow?",
    placeholder: "$0.00",
  },
  {
    key: "mainReason",
    type: "select",
    required: true,
    label: "What is the main reason you are seeking this finance?",
    options: OPTIONS.mainReason,
  },
  {
    key: "mainReasonOtherDetails",
    type: "textarea",
    required: true,
    label: "Please specify the purpose",
    conditional: (a) => a.mainReason === "Other" || a.mainReason === "TopUp",
    placeholder: "Provide details…",
  },

  {
    key: "loanPriority",
    type: "select",
    required: true,
    label: "Thinking about your loan, what is most important?",
    options: OPTIONS.loanPriority,
  },
  {
    key: "loanPriorityDetails",
    type: "textarea",
    required: true,
    label: "Please provide more information",
    conditional: (a) => a.loanPriority === "Other",
    placeholder: "Tell us what matters most to you…",
  },
  {
    key: "loanNeededByDate",
    type: "input",
    required: true,
    label: "What date do you need to obtain the loan by?",
    conditional: (a) => a.loanPriority === "Time",
    placeholder: "E.g. 15/03/2026",
  },

  {
    key: "loanTermPreference",
    type: "select",
    required: true,
    label: "What loan term would you like?",
    options: OPTIONS.loanTerm,
  },
  {
    key: "setLoanTermDetails",
    type: "input",
    required: true,
    label: "What loan term do you require? (years)",
    conditional: (a) => a.loanTermPreference === "SetTerm",
    placeholder: "E.g. 25",
    inputMode: "numeric",
  },
  {
    key: "repaymentType",
    type: "select",
    required: true,
    label: "Are you looking for a specific repayment type?",
    options: OPTIONS.repaymentType,
  },
  {
    key: "rateType",
    type: "select",
    required: true,
    label: "Are you looking for a specific rate type?",
    options: OPTIONS.rateType,
  },
  {
    key: "rateLockImportance",
    type: "radio",
    required: true,
    label:
      "Rate Lock (note this is available for Fixed Rate loans only and a fee may be payable to the lender to lock in a fixed rate)",
    options: OPTIONS.rateLockImportance,
  },

  {
    key: "spendingUnderstanding",
    type: "select",
    required: true,
    label: "Do you feel you have a good understanding of your spending and household finances?",
    options: OPTIONS.financeUnderstanding,
  },
  {
    key: "willingChangeSpending",
    type: "select",
    required: true,
    label:
      "If it impacted the amount you could borrow, would you be willing to change your spending habits?",
    options: OPTIONS.yesNoMaybe,
  },
  {
    key: "willingReduceDebts",
    type: "select",
    required: true,
    label:
      "If it impacted the amount you could borrow, would you be willing to reduce or cancel any other debts, such as car loans, personal loans or credit cards?",
    options: OPTIONS.yesNoMaybeNoDebts,
    extra: "Please note consolidating debts may not be appropriate for all people.",
  },
  {
    key: "otherFinanceApplications",
    type: "select",
    required: true,
    label: "Do you have any other applications for finance in progress at the moment?",
    options: OPTIONS.yesNoDetails,
  },
  {
    key: "otherFinanceApplicationsDetails",
    type: "textarea",
    required: true,
    label: "Please provide details about your applications for finance that are in progress",
    conditional: (a) => a.otherFinanceApplications === "Yes",
    placeholder: "E.g. credit card application, car loan, personal loan…",
  },
  {
    key: "taxResidentOtherCountry",
    type: "select",
    required: true,
    label: "Are you a tax resident of another country?",
    options: OPTIONS.yesNo,
  },
  {
    key: "retirementAge",
    type: "input",
    required: true,
    label: "At what age are you planning to retire?",
    placeholder: "E.g. 70",
    inputMode: "numeric",
  },
  {
    key: "lifestyleChangesImpactRepayments",
    type: "select",
    required: true,
    label:
      "Other than retirement, do you anticipate any changes to your lifestyle or circumstances, such as a decrease in income or increase in outgoings, which could impact you making repayments?",
    options: OPTIONS.yesNoDetails,
  },
  {
    key: "lifestyleChangesDetails",
    type: "textarea",
    required: true,
    label: "Please provide details about the changes you anticipate",
    conditional: (a) => a.lifestyleChangesImpactRepayments === "Yes",
    placeholder: "E.g. maternity leave, reduced hours, moving jobs, major expenses…",
  },

  {
    key: "incomeProtectionInsurance",
    type: "select",
    required: true,
    label: "Do you have adequate income protection insurance?",
    options: OPTIONS.yesNoUnsure,
  },
  {
    key: "debtProtectionInsurance",
    type: "select",
    required: true,
    label: "Do you have adequate debt protection insurance?",
    options: OPTIONS.yesNoUnsure,
  },
  {
    key: "lifeInsurance",
    type: "select",
    required: true,
    label: "Do you have adequate life insurance?",
    options: OPTIONS.yesNoUnsure,
  },
  {
    key: "houseContentsInsurance",
    type: "select",
    required: true,
    label: "Do you have adequate house and contents insurance?",
    options: OPTIONS.yesNoUnsure,
  },
]

const getVisibleQuestions = (questions, answers) =>
  questions.filter((q) => (typeof q.conditional === "function" ? q.conditional(answers) : true))

const getInitialApplicantState = (existing = {}) => {
  const def = {
    objectives: "",
    borrowAmount: null,
    mainReason: "",
    mainReasonOtherDetails: "",

    loanPriority: "",
    loanPriorityDetails: "",
    loanNeededByDate: "",

    loanTermPreference: "",
    setLoanTermDetails: "",
    repaymentType: "",
    rateType: "",
    rateLockImportance: "NotApplicable",

    spendingUnderstanding: "",
    willingChangeSpending: "",
    willingReduceDebts: "",
    otherFinanceApplications: "",
    otherFinanceApplicationsDetails: "",
    taxResidentOtherCountry: "",
    retirementAge: "",
    lifestyleChangesImpactRepayments: "",
    lifestyleChangesDetails: "",

    incomeProtectionInsurance: "Unsure",
    debtProtectionInsurance: "Unsure",
    lifeInsurance: "Unsure",
    houseContentsInsurance: "Unsure",
  }

  const incoming = { ...existing }
  if (incoming.borrowAmount !== null && incoming.borrowAmount !== undefined) {
    incoming.borrowAmount =
      typeof incoming.borrowAmount === "number"
        ? incoming.borrowAmount
        : parseCurrencyToNumber(incoming.borrowAmount)
  }

  return { ...def, ...incoming }
}

function NeedsObjectives({ data = {}, updateCrmData, onUpdate }) {
  const applicantsRaw = useMemo(() => normaliseApplicants(data), [data])
  const applicant2Present = useMemo(() => hasApplicant2(applicantsRaw), [applicantsRaw])

  const applicants = useMemo(() => {
    const a1 = applicantsRaw[0] || { applicantIndex: 1 }
    const a2 = applicantsRaw[1] || { applicantIndex: 2 }
    return applicant2Present ? [a1, a2] : [a1]
  }, [applicantsRaw, applicant2Present])

  const questions = useMemo(() => buildQuestions(), [])

  const initial = useMemo(() => {
    const existing = data?.needsObjectives?.byApplicant || data?.needsObjectives || {}
    const a1 = getInitialApplicantState(existing?.[1] || existing?.applicant1 || {})
    const a2 = getInitialApplicantState(existing?.[2] || existing?.applicant2 || {})
    return { 1: a1, 2: a2 }
  }, [data])

  const [byApplicant, setByApplicant] = useState({
    1: getInitialApplicantState(),
    2: getInitialApplicantState(),
  })
  const [pageByApplicant, setPageByApplicant] = useState({ 1: 0, 2: 0 })
  const [errors, setErrors] = useState([])
  const [hydrated, setHydrated] = useState(false)

  const [currencyDraft, setCurrencyDraft] = useState({ 1: "", 2: "" })
  const [currencyFocused, setCurrencyFocused] = useState({ 1: false, 2: false })

  useEffect(() => {
    if (hydrated) return
    setByApplicant(initial)
    setHydrated(true)
  }, [hydrated, initial])

  const commitToParent = useCallback(
    (nextByApplicant) => {
      const payload = {
        needsObjectives: {
          byApplicant: {
            1: nextByApplicant[1],
            ...(applicant2Present ? { 2: nextByApplicant[2] } : {}),
          },
        },
      }

      if (typeof updateCrmData === "function") updateCrmData(payload)
      else if (typeof onUpdate === "function") onUpdate(payload)
    },
    [updateCrmData, onUpdate, applicant2Present]
  )

  const updateApplicant = (idx, patch) => {
    setByApplicant((prev) => {
      const next = { ...prev, [idx]: { ...prev[idx], ...patch } }
      if (patch.rateType && patch.rateType === "Variable") {
        next[idx].rateLockImportance = next[idx].rateLockImportance || "NotApplicable"
      }
      commitToParent(next)
      return next
    })
  }

  const clampPage = (idx, currentByApplicant = byApplicant) => {
    const answers = currentByApplicant[idx] || {}
    const visible = getVisibleQuestions(questions, answers)
    const maxPage = Math.max(0, Math.ceil(visible.length / QUESTIONS_PER_PAGE) - 1)
    setPageByApplicant((p) => ({ ...p, [idx]: Math.min(p[idx] || 0, maxPage) }))
  }

  useEffect(() => {
    applicants.forEach((a, i) => {
      const idx = a?.applicantIndex || i + 1
      clampPage(idx)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byApplicant, applicants.length])

  const validatePage = (idx) => {
    const answers = byApplicant[idx] || {}
    const visible = getVisibleQuestions(questions, answers)
    const page = pageByApplicant[idx] || 0
    const slice = visible.slice(
      page * QUESTIONS_PER_PAGE,
      page * QUESTIONS_PER_PAGE + QUESTIONS_PER_PAGE
    )

    const nextErrors = []

    slice.forEach((q) => {
      if (!q.required) return
      const v = answers[q.key]
      const isEmpty = v === null || v === undefined || (typeof v === "string" && v.trim() === "")
      if (isEmpty) nextErrors.push(`Applicant ${idx}: Please answer “${q.label}”.`)

      if (q.key === "retirementAge" && !isEmpty) {
        const n = Number(String(v).replace(/[^0-9.]/g, ""))
        if (Number.isNaN(n) || n < 40 || n > 90)
          nextErrors.push(`Applicant ${idx}: Please enter a retirement age between 40 and 90.`)
      }

      if (q.key === "borrowAmount" && !isEmpty) {
        const n = Number(v)
        if (!Number.isFinite(n) || n < 10000)
          nextErrors.push(`Applicant ${idx}: Please enter a valid borrow amount.`)
      }

      if (q.key === "setLoanTermDetails" && !isEmpty) {
        const n = Number(String(v).replace(/[^0-9.]/g, ""))
        if (Number.isNaN(n) || n < 1 || n > 40)
          nextErrors.push(`Applicant ${idx}: Please enter a loan term between 1 and 40 years.`)
      }
    })

    setErrors(nextErrors)
    return nextErrors.length === 0
  }

  const goNextPage = (idx) => {
    if (!validatePage(idx)) return
    const answers = byApplicant[idx] || {}
    const visible = getVisibleQuestions(questions, answers)
    const maxPage = Math.max(0, Math.ceil(visible.length / QUESTIONS_PER_PAGE) - 1)
    setErrors([])
    setPageByApplicant((p) => ({ ...p, [idx]: Math.min((p[idx] || 0) + 1, maxPage) }))
  }

  const goPrevPage = (idx) => {
    setErrors([])
    setPageByApplicant((p) => ({ ...p, [idx]: Math.max((p[idx] || 0) - 1, 0) }))
  }

  const renderSelect = (idx, q) => {
    const answers = byApplicant[idx] || {}
    const value = answers[q.key] ?? ""
    return (
      <select
        style={styles.select}
        value={value}
        onChange={(e) => updateApplicant(idx, { [q.key]: e.target.value })}
      >
        <option value="" disabled>
          Select…
        </option>
        {q.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    )
  }

  const renderRadio = (idx, q) => {
    const answers = byApplicant[idx] || {}
    const value = answers[q.key] ?? ""
    return (
      <div style={styles.radioGroup}>
        {q.options.map((o) => {
          const checked = value === o.value
          return (
            <label
              key={o.value}
              style={{ ...styles.radioOption, borderColor: checked ? BRAND.text : "#E3E3E3" }}
              onClick={() => updateApplicant(idx, { [q.key]: o.value })}
            >
              <input
                type="radio"
                name={`${idx}-${q.key}`}
                checked={checked}
                onChange={() => updateApplicant(idx, { [q.key]: o.value })}
                style={styles.radioDot}
              />
              <span style={{ fontWeight: 800, color: BRAND.text }}>{o.label}</span>
            </label>
          )
        })}
      </div>
    )
  }

  const renderCurrency = (idx, q) => {
    const answers = byApplicant[idx] || {}
    const n = answers[q.key] ?? null

    const isFocused = Boolean(currencyFocused[idx])
    const draft = currencyDraft[idx] ?? ""
    const displayValue = isFocused ? draft : n === null ? "" : formatCurrencyAU(n)

    return (
      <input
        style={styles.input}
        value={displayValue}
        placeholder={q.placeholder || "$0.00"}
        inputMode="decimal"
        onFocus={() => {
          setCurrencyFocused((p) => ({ ...p, [idx]: true }))
          const existing = n === null ? "" : String(Number(n).toFixed(2)).replace(/\.00$/, "")
          setCurrencyDraft((p) => ({ ...p, [idx]: existing }))
        }}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9.]/g, "")
          const parts = raw.split(".")
          const safe = parts.length <= 2 ? raw : `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`
          const safe2 = safe.includes(".")
            ? `${safe.split(".")[0]}.${(safe.split(".")[1] || "").slice(0, 2)}`
            : safe

          setCurrencyDraft((p) => ({ ...p, [idx]: safe2 }))

          const parsed = parseCurrencyToNumber(safe2)
          updateApplicant(idx, { [q.key]: parsed })
        }}
        onBlur={() => {
          setCurrencyFocused((p) => ({ ...p, [idx]: false }))
          const parsed = parseCurrencyToNumber(currencyDraft[idx])
          updateApplicant(idx, { [q.key]: parsed })
          setCurrencyDraft((p) => ({ ...p, [idx]: "" }))
          clampPage(idx)
        }}
      />
    )
  }

  const renderField = (idx, q) => {
    const answers = byApplicant[idx] || {}
    const value = answers[q.key] ?? ""

    if (q.type === "textarea") {
      return (
        <textarea
          style={styles.textarea}
          value={value}
          onChange={(e) => updateApplicant(idx, { [q.key]: e.target.value })}
          placeholder={q.placeholder || ""}
        />
      )
    }

    if (q.type === "input") {
      return (
        <input
          style={styles.input}
          value={value}
          onChange={(e) => updateApplicant(idx, { [q.key]: e.target.value })}
          placeholder={q.placeholder || ""}
          inputMode={q.inputMode || "text"}
        />
      )
    }

    if (q.type === "currency") return renderCurrency(idx, q)
    if (q.type === "radio") return renderRadio(idx, q)
    return renderSelect(idx, q)
  }

  const renderApplicantBlock = (idx) => {
    const answers = byApplicant[idx] || {}
    const visible = getVisibleQuestions(questions, answers)
    const page = pageByApplicant[idx] || 0
    const maxPage = Math.max(0, Math.ceil(visible.length / QUESTIONS_PER_PAGE) - 1)

    const pageQuestions = visible.slice(
      page * QUESTIONS_PER_PAGE,
      page * QUESTIONS_PER_PAGE + QUESTIONS_PER_PAGE
    )
    const isLast = page >= maxPage
    const isFirst = page === 0

    return (
      <div style={styles.section}>
        <p style={styles.sectionTitle}>Applicant {idx} Questions</p>

        {pageQuestions.map((q) => (
          <div key={q.key} style={styles.field}>
            <label style={styles.label}>
              {q.required && <span style={styles.requiredStar}>*</span>}
              {q.label}
            </label>

            {renderField(idx, q)}

            {q.extra && <div style={styles.hint}>{q.extra}</div>}
          </div>
        ))}

        <div style={styles.pager}>
          <button
            type="button"
            onClick={() => goPrevPage(idx)}
            style={{ ...styles.pagerBtn, ...(isFirst ? styles.pagerBtnDisabled : {}) }}
            disabled={isFirst}
          >
            ← Back
          </button>

          <div style={styles.pagerText}>
            Page {page + 1} of {maxPage + 1}
          </div>

          {!isLast ? (
            <button type="button" onClick={() => goNextPage(idx)} style={styles.pagerBtnPrimary}>
              Next →
            </button>
          ) : (
            <div style={{ width: 260 }} />
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h2 style={styles.h2}>Needs &amp; Objectives</h2>
      </div>

      <div style={styles.cardsRow}>
        <div style={styles.card}>
          <p style={styles.cardTitle}>Applicant 1</p>
          <div style={styles.cardSub}>{getApplicantLabel(applicants[0], 1)}</div>
          <div style={styles.cardMeta}>{getApplicantEmail(applicants[0]) || "No email added"}</div>
        </div>

        {applicant2Present ? (
          <div style={styles.card}>
            <p style={styles.cardTitle}>Applicant 2</p>
            <div style={styles.cardSub}>{getApplicantLabel(applicants[1], 2)}</div>
            <div style={styles.cardMeta}>{getApplicantEmail(applicants[1]) || "No email added"}</div>
          </div>
        ) : (
          <div style={styles.cardMuted}>+ Add Applicant</div>
        )}
      </div>

      {renderApplicantBlock(1)}
      {applicant2Present && renderApplicantBlock(2)}

      {errors.length > 0 && (
        <div style={styles.errorBox}>
          {errors.map((e, i) => (
            <div key={i}>• {e}</div>
          ))}
        </div>
      )}
    </div>
  )
}

export default NeedsObjectives